// src/features/courses/hooks/useCourses.js
import { useCallback, useState } from 'react';
import { coursesApi } from '../services/coursesApi';
import { CONFIG } from '../../../config';

const DEFAULT_PAGE_SIZE = 12;

/**
 * Encapsulates loading dashboard data, optimistic bookmark, and delete + local hide.
 * No UI; container supplies auth flags + user.
 */
export function useCourses({ authEnabled, user, ready }) {
  const [courses, setCourses] = useState([]);
  const [continueLearning, setContinueLearning] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(() => {
    try {
      const saved = localStorage.getItem('dashboard.pageSize');
      return saved ? parseInt(saved, 10) : DEFAULT_PAGE_SIZE;
    } catch {
      return DEFAULT_PAGE_SIZE;
    }
  });
  const [offset, setOffset] = useState(0);

  const userId = authEnabled ? (user?.uid || '') : CONFIG.USER_ID;

  const load = useCallback(async (paginationOverride = {}) => {
    if (authEnabled && (!ready || !user)) {
      setLoading(false);
      return;
    }
    setStatus('');
    setLoading(true);

    const reqLimit = paginationOverride.limit ?? limit;
    const reqOffset = paginationOverride.offset ?? offset;

    try {
      const data = await coursesApi.fetchOverview({
        userId,
        auth: authEnabled,
        limit: reqLimit,
        offset: reqOffset,
      });
      const list = Array.isArray(data?.courses) ? data.courses : [];

      // Update pagination state from response
      if (typeof data?.totalCount === 'number') {
        setTotalCount(data.totalCount);
      }
      if (typeof data?.limit === 'number') {
        setLimit(data.limit);
      }
      if (typeof data?.offset === 'number') {
        setOffset(data.offset);
      }

      // Use server-derived continue learning (consistent with course card data)
      setContinueLearning(data?.continueLearning ?? null);

      // respect locally hidden (deleted) courses
      try {
        const key = `deletedCourses:${userId}`;
        const hidden = new Set(JSON.parse(localStorage.getItem(key) || '[]'));
        setCourses(list.filter((c) => !hidden.has(c.courseId)));
      } catch {
        setCourses(list);
      }
    } catch (err) {
      setStatus(String(err?.message || 'Failed to load dashboard.'));
    } finally {
      setLoading(false);
    }
  }, [authEnabled, ready, user, userId, limit, offset]);

  // Load ALL courses (no pagination) for filtering/searching
  const loadAll = useCallback(async () => {
    if (authEnabled && (!ready || !user)) {
      setLoading(false);
      return;
    }
    setStatus('');
    setLoading(true);

    try {
      const data = await coursesApi.fetchOverview({
        userId,
        auth: authEnabled,
        limit: 9999, // Large number to get all courses
        offset: 0,
      });
      const list = Array.isArray(data?.courses) ? data.courses : [];

      // Update total count
      if (typeof data?.totalCount === 'number') {
        setTotalCount(data.totalCount);
      }

      // respect locally hidden (deleted) courses
      try {
        const key = `deletedCourses:${userId}`;
        const hidden = new Set(JSON.parse(localStorage.getItem(key) || '[]'));
        setCourses(list.filter((c) => !hidden.has(c.courseId)));
      } catch {
        setCourses(list);
      }
    } catch (err) {
      setStatus(String(err?.message || 'Failed to load dashboard.'));
    } finally {
      setLoading(false);
    }
  }, [authEnabled, ready, user, userId]);

  // Pagination helpers
  const totalPages = Math.ceil(totalCount / limit) || 1;
  const currentPage = Math.floor(offset / limit) + 1;

  const goToPage = useCallback((page) => {
    const newOffset = (page - 1) * limit;
    setOffset(newOffset);
    load({ offset: newOffset, limit });
  }, [limit, load]);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  }, [currentPage, totalPages, goToPage]);

  const prevPage = useCallback(() => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, goToPage]);

  const changePageSize = useCallback((newLimit) => {
    try {
      localStorage.setItem('dashboard.pageSize', String(newLimit));
    } catch {}
    setLimit(newLimit);
    setOffset(0);
    load({ offset: 0, limit: newLimit });
  }, [load]);

  const toggleBookmark = useCallback(
    async (courseId) => {
      // Optimistic UI; server call best-effort
      setCourses((prev) =>
        prev.map((c) => (c.courseId === courseId ? { ...c, bookmarked: courseId } : c))
      );
      try {
        await coursesApi.setBookmark({ courseId: courseId, auth: authEnabled });
      } catch {

      }
    },
    [authEnabled]
  );

  const deleteCourse = useCallback(
    async (courseId) => {
      let success = false;
      try {
        await coursesApi.deleteCourse({ courseId, userId, auth: authEnabled });
        success = true;
      } catch (e) {
        setStatus(
          `Failed to delete on server. It will be hidden locally. ${
            e?.message ? String(e.message) : ''
          }`.trim()
        );
      } finally {
        // always remove from UI + persist local hide
        setCourses((prev) => prev.filter((c) => c.courseId !== courseId));
        try {
          const key = `deletedCourses:${userId}`;
          const arr = JSON.parse(localStorage.getItem(key) || '[]');
          if (!arr.includes(courseId)) arr.push(courseId);
          localStorage.setItem(key, JSON.stringify(arr));
        } catch {}
      }
      return success;
    },
    [authEnabled, userId]
  );

  return {
    courses,
    continueLearning,
    status,
    loading,
    load,
    loadAll,
    toggleBookmark,
    deleteCourse,
    // Pagination
    pagination: {
      totalCount,
      totalPages,
      currentPage,
      limit,
      offset,
      goToPage,
      nextPage,
      prevPage,
      changePageSize,
    },
  };
}